import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import Heading from '@starbucks-web/pattern-library/lib/components/heading';
import ImageLoader from '@starbucks-web/pattern-library/lib/components/image-loader';
import Button from '@starbucks-web/pattern-library/lib/components/button';
import Icon from '@starbucks-web/pattern-library/lib/components/icon';
import chevronRight from '@starbucks-web/pattern-library/lib/icons/chevron-right';

import LoyaltyCard from '../shared/loyalty-card';
import styles from './styles.cssm';
import { STATUS_LEVELS } from '../shared/constants';

/**
 * Individual compartment within the bento box
 * @returns {JSX.Element} The bento compartment component
 */
const BentoCompartment = ({
  image,
  heading,
  body,
  cta,
  rewardsLevel = STATUS_LEVELS.GREEN,
  containerClasses,
  ...otherProps
}) => {
  return (
    <LoyaltyCard
      {...otherProps}
      containerClasses={cs('height-100 p3 md-p4', containerClasses)}
      disableBoxShadow
      rewardsLevel={rewardsLevel.toLowerCase()}
    >
      <div
        className={cs(
          styles.compartmentGap,
          'flex flex-column height-100 justify-center items-center text-center'
        )}
      >
        {image && (
          <div className="flex-shrink-none flex items-center justify-center">
            {typeof image === 'string' ? (
              <ImageLoader
                alt=""
                aria-hidden="true"
                className={styles.compartmentImage}
                includeFallback={{
                  enabled: true,
                  fallbackClassName: 'bg-white',
                }}
                lazyLoad={{ enabled: false }}
                role="presentation"
                src={image}
                wrapInFlex={{
                  enabled: true,
                  constrainWidth: false,
                  flexEmbedWrapperClassName: styles.imageContainer,
                }}
              />
            ) : (
              <div aria-hidden="true" role="presentation">
                {image}
              </div>
            )}
          </div>
        )}

        <div
          className={cs(
            styles.compartmentGap,
            'flex flex-column flex-auto justify-center items-center text-center'
          )}
        >
          {heading && (
            <Heading
              className="text-bold m0 text-center"
              size="sm"
              tagName="h3"
            >
              {heading}
            </Heading>
          )}

          {body && (
            <div className="m0 text-center flex flex-column justify-center flex-auto text-xs lineHeight-normal">
              {body}
            </div>
          )}
        </div>

        {cta && (
          <div
            className={cs(
              styles.ctaWrapper,
              'flex justify-center items-center mt-auto'
            )}
          >
            {typeof cta === 'object' && cta.label ? (
              <Button
                className="flex text-xxs text-normal text-noUnderline color-textBodyOnPrimary bg-white border-none px3 py2"
                href={cta.href}
                onClick={cta.onClick}
                visualStyle="textLink"
                {...cta.buttonProps}
              >
                <span className="flex items-center justify-between">
                  <span className="text-left">{cta.label}</span>
                  <Icon path={chevronRight} size="16px" />
                </span>
              </Button>
            ) : (
              cta
            )}
          </div>
        )}
      </div>
    </LoyaltyCard>
  );
};

BentoCompartment.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  cta: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
  rewardsLevel: PropTypes.oneOf([
    STATUS_LEVELS.GREEN,
    STATUS_LEVELS.GOLD,
    STATUS_LEVELS.RESERVE,
  ]),
  containerClasses: PropTypes.string,
};

export default BentoCompartment;
